<script setup lang="ts">
import { XCircleIcon } from '@heroicons/vue/solid'
import { postUploadComplianceProfileDocument } from '../../utilities/api'
import { computed, onMounted, ref } from 'vue'
import BaseInputFilePond from './BaseInputFilePond.vue'
import { RenderFunction, watch } from 'vue'
import { useField } from 'vee-validate'
import { useVModel } from '@vueuse/core'
import BaseButtonMuted from './BaseButtonMuted.vue'
import BaseButtonPrimary from './BaseButtonPrimary.vue'

const props = defineProps<{
    modelValue: string
    label: string
    name: string
    icon?: RenderFunction
    required?: boolean
    required2?: boolean
    rules?: any
}>()

const emit = defineEmits(['update:modelValue', 'update:uploading'])

const uploading = ref(false)
watch(uploading, () => emit('update:uploading', uploading.value))

const uploadedFile = useVModel(props, 'modelValue', emit)

watch(uploadedFile, () => {
    updateValue(uploadedFile.value)
    if (!uploadedFile?.value) {
        showUpload.value = true
    }
})

const { errorMessage, handleChange } = useField(props.name, props.rules, { initialValue: props.modelValue })

const showUpload = ref(true)

function updateValue(value) {
    handleChange(value)
}

/**
 *
 * @param fieldName
 * @param file
 * @param metadata
 * @param load
 * @param error
 * @param progress
 * @param abort
 * @param transfer
 * @param options
 *
 * docs: https://pqina.nl/filepond/docs/api/server/#process-1
 */
async function process(fieldName, file, metadata, load, error, progress, abort, transfer, options) {
    const controller = new AbortController()
    uploading.value = true
    await postUploadComplianceProfileDocument(file, file.name, {
        // Should call the progress method to update the progress to 100% before calling load
        // Setting computable to false switches the loading indicator to infinite mode
        onUploadProgress: (e) => progress(e.lengthComputable, e.loaded, e.total),
        signal: controller.signal,
    })
        .then(({ data }) => {
            // Should call the load method when done and pass the returned server file id
            // this server file id is then used later on when reverting or restoring a file
            // so your server knows which file to return without exposing that info to the client
            uploadedFile.value = data
            uploading.value = false
            load(data)
        })
        .catch(() => {
            error('Upload failed')
            uploading.value = false
        })

    return {
        abort: () => {
            // This function is entered if the user has tapped the cancel button
            controller.abort()
            // Let FilePond know the request has been cancelled
            abort()
        },
    }
}

function openLink(link) {
    window.open(link, '_blank').focus()
}

onMounted(() => {
    if (uploadedFile && uploadedFile.value) {
        showUpload.value = false
    }
})

/**
 * Display the file name if it existed before. If the file was just uploaded, display the name extracted from key instead.
 */
const displayFileName = computed(() => uploadedFile.value.file_name ?? uploadedFile.value.key?.split('/')[1])
</script>

<template>
    <div>
        <BaseInputFilePond
            v-if="showUpload"
            ref="uploader"
            :label="label"
            :process="process"
            :icon="props.icon"
            :name="props.name"
            :required="props.required"
            :required2="props.required2"
            :uploaded-file="uploadedFile"
            :error-message="errorMessage"
            :accepted-file-types="['image/jpg', 'image/jpeg', 'image/png', 'application/pdf']"
            @flush="uploadedFile = null"
            @update:uploading="emit"></BaseInputFilePond>
        <div v-else>
            <p class="pb-1 text-sm text-gray-700">{{ props.label }}</p>
            <div class="flex items-center justify-between rounded-xl border border-gray-200 bg-gray-50 px-5 py-4">
                <div class="flex items-center justify-center gap-2 text-sm text-gray-700">
                    {{ displayFileName }}
                    <button type="button" @click="uploadedFile = null">
                        <component :is="XCircleIcon" class="h-5 w-5"></component>
                    </button>
                </div>
                <div class="flex space-x-2">
                    <BaseButtonPrimary v-if="uploadedFile.url" @click="openLink(uploadedFile.url)">{{
                        $t('compliance_profile.show_document')
                    }}</BaseButtonPrimary>
                    <BaseButtonMuted @click="showUpload = true">{{
                        $t('compliance_profile.upload_document')
                    }}</BaseButtonMuted>
                </div>
            </div>
        </div>
    </div>
</template>
